/**
 * AME System Deflection Calculator - Pro Edition
 * Six load scenarios (Distributed & Central force application x On Two Supports /
 * Fixed at One End / Fixed at Both Ends), per AME-SYS-A014 deflection calculation diagrams.
 * All deflection results include the profile's self-weight (d = dg + dF).
 */

(function() {
  'use strict';

  const E_ALUMINIUM = 70000;   // Young's modulus, N/mm²
  const GRAVITY = 9.81;        // m/s²
  const YIELD_STRENGTH = 195;  // N/mm²
  const BASE_URL = 'https://amesystem.com.au';

  // AME System Profile database with product URLs
  const PROFILE_DATABASE = {
    '30': {
      name: '30 Series',
      profiles: [
        { id: 'AL-3030', name: 'AL-3030 (30×30)', Ix: 2.44, Iy: 2.44, Wy: 1.62, Wx: 1.62, A: 2.79, M: 0.75, url: '/products/30-30-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-3030-90', name: 'AL-3030-90 (30×30 90°)', Ix: 2.69, Iy: 2.69, Wy: 1.79, Wx: 1.79, A: 3.12, M: 0.84, url: '/products/30-30-90-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-3040', name: 'AL-3040 (30×40)', Ix: 6.19, Iy: 3.72, Wy: 2.48, Wx: 3.09, A: 4.35, M: 1.18, url: '/products/30-40-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-3060', name: 'AL-3060 (30×60)', Ix: 4.73, Iy: 17.47, Wy: 5.82, Wx: 3.15, A: 5.16, M: 1.39, url: '/products/30-60-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-3060-90', name: 'AL-3060-90 (30×60 90°)', Ix: 5.53, Iy: 20.44, Wy: 6.81, Wx: 3.68, A: 5.63, M: 1.52, url: '/products/30-60-90-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-3080', name: 'AL-3080 (30×80)', Ix: 11.73, Iy: 27.47, Wy: 6.86, Wx: 7.82, A: 6.10, M: 1.67, url: '/products/30-80-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-6060', name: 'AL-6060 (60×60)', Ix: 30.52, Iy: 30.52, Wy: 10.84, Wx: 10.84, A: 8.03, M: 2.17, url: '/products/60-60-t-slot-aluminium-extrusion-profile' }
      ]
    },
    '40': {
      name: '40 Series',
      profiles: [
        { id: 'AL-4040', name: 'AL-4040 (40×40)', Ix: 8.16, Iy: 8.16, Wy: 4.08, Wx: 4.08, A: 5.84, M: 1.58, url: '/products/40-40-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-4040-90', name: 'AL-4040-90 (40×40 90°)', Ix: 8.82, Iy: 8.82, Wy: 4.41, Wx: 4.41, A: 6.18, M: 1.67, url: '/products/40-40-90-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-4080', name: 'AL-4080 (40×80)', Ix: 17.94, Iy: 63.45, Wy: 15.86, Wx: 8.97, A: 11.37, M: 3.07, url: '/products/40-80-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-4080-90', name: 'AL-4080-90 (40×80 90°)', Ix: 20.98, Iy: 74.24, Wy: 18.56, Wx: 10.49, A: 11.89, M: 3.41, url: '/products/40-80-90-t-slot-aluminium-extrusion-profile' },
        { id: 'AL-8080', name: 'AL-8080 (80×80)', Ix: 125.49, Iy: 125.49, Wy: 31.37, Wx: 31.37, A: 18.45, M: 5.00, url: '/products/80-80-t-slot-aluminium-extrusion-profile' }
      ]
    }
  };

  // Load scenarios per AME-SYS-A014.
  // Deflection: d = coef · F · L³ / (E · I · 10⁴)   (I entered in cm⁴, ×10⁴ converts to mm⁴)
  // Self-weight acts as a distributed load, so dg always uses the distributed
  // coefficient of the support type (dgCoef).
  // Bending stress: σ = F · L / (stressK · W · 10³)  (W entered in cm³, ×10³ converts to mm³)
  const SCENARIOS = [
    {
      id: 'dist-supported', group: 'distributed', groupName: 'Distributed Load',
      support: 'supported', name: 'On Two Supports',
      dfCoef: 5 / 384, dgCoef: 5 / 384, stressK: 8,
      dfFormula: 'd<sub>F</sub> = 5·F·L³ / (384·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = 5·F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'dist-fixed-one', group: 'distributed', groupName: 'Distributed Load',
      support: 'fixed-one', name: 'Fixed at One End',
      dfCoef: 1 / 8, dgCoef: 1 / 8, stressK: 2,
      dfFormula: 'd<sub>F</sub> = F·L³ / (8·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (8·E·I·10⁴)'
    },
    {
      id: 'dist-fixed-both', group: 'distributed', groupName: 'Distributed Load',
      support: 'fixed-both', name: 'Fixed at Both Ends',
      dfCoef: 1 / 384, dgCoef: 1 / 384, stressK: 12,
      dfFormula: 'd<sub>F</sub> = F·L³ / (384·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'central-supported', group: 'central', groupName: 'Central (Point) Load',
      support: 'supported', name: 'On Two Supports',
      dfCoef: 1 / 48, dgCoef: 5 / 384, stressK: 4,
      dfFormula: 'd<sub>F</sub> = F·L³ / (48·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = 5·F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    },
    {
      id: 'central-fixed-one', group: 'central', groupName: 'Central (Point) Load',
      support: 'fixed-one', name: 'Fixed at One End',
      dfCoef: 1 / 3, dgCoef: 1 / 8, stressK: 1,
      dfFormula: 'd<sub>F</sub> = F·L³ / (3·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (8·E·I·10⁴)'
    },
    {
      id: 'central-fixed-both', group: 'central', groupName: 'Central (Point) Load',
      support: 'fixed-both', name: 'Fixed at Both Ends',
      dfCoef: 1 / 192, dgCoef: 1 / 384, stressK: 8,
      dfFormula: 'd<sub>F</sub> = F·L³ / (192·E·I·10⁴)',
      dgFormula: 'd<sub>g</sub> = F<sub>g</sub>·L³ / (384·E·I·10⁴)'
    }
  ];

  let lastCalc = null;

  function getScenario(id) {
    return SCENARIOS.find(s => s.id === id) || null;
  }

  // Get all profiles as flat array
  function getAllProfiles() {
    const all = [];
    Object.keys(PROFILE_DATABASE).forEach(series => {
      PROFILE_DATABASE[series].profiles.forEach(p => {
        all.push({ ...p, series });
      });
    });
    return all;
  }

  // Get profile by ID
  function getProfileById(id) {
    for (const series of Object.values(PROFILE_DATABASE)) {
      const found = series.profiles.find(p => p.id === id);
      if (found) return found;
    }
    return null;
  }

  // Full calculation for one scenario: self-weight force, deflection due to own
  // weight, deflection due to the acting force, total deflection, bending stress.
  function computeScenario(scenario, F, L, I, W, M) {
    const L3 = Math.pow(L, 3);
    const EI = E_ALUMINIUM * I * 10000;
    const fg = M * (L / 1000) * GRAVITY;
    const dg = (scenario.dgCoef * fg * L3) / EI;
    const df = (scenario.dfCoef * F * L3) / EI;
    const sigma = (F * L) / (scenario.stressK * W * 1000);
    return { fg, dg, df, total: dg + df, sigma };
  }

  // ============ DIAGRAMS ============
  const CLR = { beam: '#005AB5', load: '#d99e00', defl: '#e74c3c', dim: '#777', support: '#8a8a8a' };

  function svgWall(xa, xb) {
    let out = `<rect x="${xa}" y="14" width="${xb - xa}" height="52" fill="${CLR.support}"/>`;
    for (let y = 16; y <= 56; y += 10) {
      out += `<line x1="${xa + 1}" y1="${y}" x2="${xb - 1}" y2="${y + 8}" stroke="#6d6d6d" stroke-width="1"/>`;
    }
    return out;
  }

  function svgPointLoad(x) {
    return `
      <line x1="${x}" y1="10" x2="${x}" y2="28" stroke="${CLR.load}" stroke-width="2.5"/>
      <polygon points="${x},36 ${x - 4.5},27 ${x + 4.5},27" fill="${CLR.load}"/>
      <text x="${x + 7}" y="17" fill="${CLR.load}" font-size="11" font-weight="bold">F</text>`;
  }

  function svgDistributedLoad(x1, x2) {
    let out = `<line x1="${x1}" y1="12" x2="${x2}" y2="12" stroke="${CLR.load}" stroke-width="1.5"/>`;
    const n = 5;
    for (let i = 0; i < n; i++) {
      const x = x1 + ((x2 - x1) / (n - 1)) * i;
      out += `
        <line x1="${x}" y1="12" x2="${x}" y2="26" stroke="${CLR.load}" stroke-width="2"/>
        <polygon points="${x},33 ${x - 3.5},25 ${x + 3.5},25" fill="${CLR.load}"/>`;
    }
    out += `<text x="${(x1 + x2) / 2 + 8}" y="9" fill="${CLR.load}" font-size="11" font-weight="bold">F</text>`;
    return out;
  }

  function svgDeflectionDim(x, s) {
    const labelY = s >= 12 ? 40 + s / 2 + 4 : 40 + s + 10;
    return `
      <line x1="${x}" y1="41" x2="${x}" y2="${40 + s}" stroke="${CLR.defl}" stroke-width="1.5" stroke-dasharray="3,2"/>
      <line x1="${x - 4}" y1="${40 + s}" x2="${x + 4}" y2="${40 + s}" stroke="${CLR.defl}" stroke-width="1.5"/>
      <text x="${x + 6}" y="${labelY}" fill="${CLR.defl}" font-size="11" font-weight="bold">D</text>`;
  }

  function svgLengthDim(x1, x2) {
    const y = 82;
    return `
      <line x1="${x1}" y1="${y}" x2="${x2}" y2="${y}" stroke="${CLR.dim}" stroke-width="1"/>
      <line x1="${x1}" y1="${y - 5}" x2="${x1}" y2="${y + 5}" stroke="${CLR.dim}" stroke-width="1"/>
      <line x1="${x2}" y1="${y - 5}" x2="${x2}" y2="${y + 5}" stroke="${CLR.dim}" stroke-width="1"/>
      <text x="${(x1 + x2) / 2 - 3}" y="${y + 14}" fill="${CLR.dim}" font-size="11" font-weight="bold">L</text>`;
  }

  // Draw load application diagram with F, L and D labelled
  function drawDiagram(svgId, scenario, deflection) {
    const svg = document.getElementById(svgId);
    if (!svg) return;
    const s = 5 + (Math.min(Math.max(deflection, 0), 40) / 40) * 15; // 5..20 px sag

    let x1, x2;
    const parts = [];

    if (scenario.support === 'supported') {
      x1 = 28; x2 = 172;
      parts.push(`<polygon points="${x1},42 ${x1 - 9},60 ${x1 + 9},60" fill="${CLR.support}"/>`);
      parts.push(`<polygon points="${x2},42 ${x2 - 9},60 ${x2 + 9},60" fill="${CLR.support}"/>`);
    } else if (scenario.support === 'fixed-one') {
      x1 = 24; x2 = 176;
      parts.push(svgWall(10, 24));
    } else {
      x1 = 24; x2 = 176;
      parts.push(svgWall(10, 24));
      parts.push(svgWall(176, 190));
    }

    // Undeflected position + deflected beam
    parts.push(`<line x1="${x1}" y1="40" x2="${x2}" y2="40" stroke="#bbb" stroke-width="1.5" stroke-dasharray="4,3"/>`);
    if (scenario.support === 'fixed-one') {
      parts.push(`<path d="M ${x1} 40 Q ${x1 + (x2 - x1) * 0.55} 40 ${x2} ${40 + s}" stroke="${CLR.beam}" stroke-width="3" fill="none"/>`);
    } else {
      parts.push(`<path d="M ${x1} 40 Q ${(x1 + x2) / 2} ${40 + 2 * s} ${x2} 40" stroke="${CLR.beam}" stroke-width="3" fill="none"/>`);
    }

    // Force application
    if (scenario.group === 'distributed') {
      parts.push(svgDistributedLoad(x1 + 10, x2 - 10));
    } else if (scenario.support === 'fixed-one') {
      parts.push(svgPointLoad(x2 - 12));
    } else {
      parts.push(svgPointLoad((x1 + x2) / 2));
    }

    // Deflection dimension D
    if (scenario.support === 'fixed-one') {
      parts.push(svgDeflectionDim(x2 + 8, s));
    } else {
      parts.push(svgDeflectionDim((x1 + x2) / 2, s));
    }

    // Length dimension L
    parts.push(svgLengthDim(x1, x2));

    svg.innerHTML = parts.join('');
  }

  // ============ RESULT CARDS ============
  function buildCards() {
    const wrap = document.getElementById('dc-cards-wrap');
    if (!wrap) return;

    let html = '';
    ['distributed', 'central'].forEach(group => {
      const scenarios = SCENARIOS.filter(s => s.group === group);
      html += `<div class="dc-group-title">${scenarios[0].groupName}</div>`;
      html += '<div class="dc-cards">';
      scenarios.forEach(sc => {
        html += `
          <div class="dc-card">
            <div class="dc-card-title">${sc.name}</div>
            <div class="dc-card-formula">${sc.dfFormula}</div>
            <div class="dc-diagram"><svg viewBox="0 0 200 100" id="dc-diagram-${sc.id}" role="img" aria-label="${sc.groupName}, ${sc.name} load diagram"></svg></div>
            <div class="dc-diagram-legend" id="dc-legend-${sc.id}">F = Force (N) · L = Length (mm) · D = Deflection (mm)</div>
            <div class="dc-stat">
              <span class="dc-stat-label">Deflection D</span>
              <span class="dc-stat-val big"><span id="dc-def-${sc.id}">-</span><span>mm</span></span>
            </div>
            <details class="dc-calc">
              <summary>Deflection calculation</summary>
              <div class="dc-calc-body" id="dc-calc-${sc.id}"></div>
            </details>
            <div class="dc-stress" id="dc-stress-${sc.id}"></div>
          </div>`;
      });
      html += '</div>';
    });
    wrap.innerHTML = html;
  }

  function renderCalcDetails(el, scenario, r, M, L) {
    el.innerHTML = `
      <p>Weight force: F<sub>g</sub> = M·L·g = ${M} kg/m × ${(L / 1000).toFixed(2)} m × ${GRAVITY} m/s² ≈ <strong>${r.fg.toFixed(2)} N</strong></p>
      <p>Deflection due to own weight: ${scenario.dgFormula} ≈ <strong>${r.dg.toFixed(2)} mm</strong></p>
      <p>Deflection due to the acting force: ${scenario.dfFormula} ≈ <strong>${r.df.toFixed(2)} mm</strong></p>
      <p>Total deflection: D = d<sub>g</sub> + d<sub>F</sub> ≈ <strong>${r.total.toFixed(1)} mm</strong></p>
      <p class="dc-calc-note">The final deflection result includes the contribution from the profile's self-weight.</p>`;
  }

  function renderStress(el, r) {
    const ok = r.sigma <= YIELD_STRENGTH;
    el.className = 'dc-stress ' + (ok ? 'dc-stress-ok' : 'dc-stress-warn');
    el.innerHTML = `
      <span class="dc-stress-val">${ok ? '✓' : '⚠'} Bending stress σ ≈ ${r.sigma.toFixed(1)} N/mm²</span>
      <span class="dc-stress-msg">${ok
        ? `Less than the yield point of ${YIELD_STRENGTH} N/mm², so the profile doesn't get permanently deformed by this force.`
        : `Greater than the yield point of ${YIELD_STRENGTH} N/mm², so the profile might get permanently deformed by this force.`}</span>`;
  }

  // Update profile dropdown
  function updateProfileDropdown(series) {
    const profileSelect = document.getElementById('dc-profile');
    if (!profileSelect) return;
    profileSelect.innerHTML = '<option value="">Select profile/SKU...</option>';
    if (series && PROFILE_DATABASE[series]) {
      PROFILE_DATABASE[series].profiles.forEach(p => {
        const opt = document.createElement('option');
        opt.value = p.id;
        opt.textContent = p.name;
        profileSelect.appendChild(opt);
      });
      profileSelect.disabled = false;
    } else {
      profileSelect.disabled = true;
    }
    performCalculation();
  }

  // Main calculation
  function performCalculation() {
    const series = document.getElementById('dc-series')?.value;
    const profileSelect = document.getElementById('dc-profile');
    const length = parseFloat(document.getElementById('dc-length')?.value);
    const load = parseFloat(document.getElementById('dc-load')?.value);
    const axis = document.getElementById('dc-axis')?.value || 'x';

    const resultsEl = document.getElementById('dc-results');

    if (!series || !profileSelect?.value || !length || length < 100 || !load || load < 1) {
      resultsEl.style.display = 'none';
      lastCalc = null;
      updateShareURL(null);
      return;
    }

    const profile = getProfileById(profileSelect.value);
    if (!profile) return;

    const I = axis === 'x' ? profile.Ix : profile.Iy;
    const W = axis === 'x' ? profile.Wx : profile.Wy;

    // Update header
    document.getElementById('dc-res-profile').textContent = profile.name;
    document.getElementById('dc-res-inertia').textContent = `I${axis} = ${I} cm⁴ · W${axis} = ${W} cm³`;
    document.getElementById('dc-res-length').textContent = `L = ${length} mm`;
    document.getElementById('dc-res-load').textContent = `F = ${load} N`;

    // Update buy button
    const buyBtn = document.getElementById('dc-buy-btn');
    if (buyBtn) {
      buyBtn.href = BASE_URL + profile.url;
      buyBtn.style.display = 'inline-flex';
    }

    // Calculate all six scenarios
    SCENARIOS.forEach(sc => {
      const r = computeScenario(sc, load, length, I, W, profile.M);

      const defEl = document.getElementById(`dc-def-${sc.id}`);
      if (defEl) defEl.textContent = r.total.toFixed(1);

      const legendEl = document.getElementById(`dc-legend-${sc.id}`);
      if (legendEl) legendEl.textContent = `F = ${load} N · L = ${length} mm · D = ${r.total.toFixed(1)} mm`;

      const calcEl = document.getElementById(`dc-calc-${sc.id}`);
      if (calcEl) renderCalcDetails(calcEl, sc, r, profile.M, length);

      const stressEl = document.getElementById(`dc-stress-${sc.id}`);
      if (stressEl) renderStress(stressEl, r);

      drawDiagram(`dc-diagram-${sc.id}`, sc, r.total);
    });

    resultsEl.style.display = 'block';

    lastCalc = { profile, length, load, axis, I, W };

    // Update share URL
    updateShareURL({ profile: profile.id, length, load, axis });

    // Update chart
    updateDeflectionChart();

    // Save to recent
    saveToRecent({ profile: profile.id, profileName: profile.name, length, load, axis, timestamp: Date.now() });
  }

  // ============ SHAREABLE URL ============
  function updateShareURL(params) {
    const shareInput = document.getElementById('dc-share-url');
    const shareSection = document.getElementById('dc-share-section');
    if (!shareInput || !shareSection) return;

    if (!params) {
      shareSection.style.display = 'none';
      return;
    }

    const url = new URL(window.location.href.split('?')[0]);
    url.searchParams.set('p', params.profile);
    url.searchParams.set('l', params.length);
    url.searchParams.set('f', params.load);
    url.searchParams.set('a', params.axis);

    shareInput.value = url.toString();
    shareSection.style.display = 'block';
  }

  function loadFromURL() {
    const params = new URLSearchParams(window.location.search);
    const profileId = params.get('p');
    const length = params.get('l');
    const load = params.get('f');
    const axis = params.get('a');

    if (profileId && length && load) {
      // Find which series this profile belongs to
      for (const [seriesKey, seriesData] of Object.entries(PROFILE_DATABASE)) {
        const found = seriesData.profiles.find(p => p.id === profileId);
        if (found) {
          document.getElementById('dc-series').value = seriesKey;
          updateProfileDropdown(seriesKey);
          setTimeout(() => {
            document.getElementById('dc-profile').value = profileId;
            document.getElementById('dc-length').value = length;
            document.getElementById('dc-load').value = load;
            if (axis) document.getElementById('dc-axis').value = axis;
            performCalculation();
          }, 50);
          break;
        }
      }
    }
  }

  function copyShareURL() {
    const shareInput = document.getElementById('dc-share-url');
    if (shareInput) {
      shareInput.select();
      document.execCommand('copy');
      const btn = document.getElementById('dc-copy-btn');
      if (btn) {
        const orig = btn.textContent;
        btn.textContent = '✓ Copied!';
        setTimeout(() => btn.textContent = orig, 2000);
      }
    }
  }

  // ============ PDF EXPORT ============
  function exportPDF() {
    const profile = getProfileById(document.getElementById('dc-profile')?.value);
    const length = parseFloat(document.getElementById('dc-length')?.value);
    const load = parseFloat(document.getElementById('dc-load')?.value);
    const axis = document.getElementById('dc-axis')?.value || 'x';

    if (!profile || !length || !load) return;

    const I = axis === 'x' ? profile.Ix : profile.Iy;
    const W = axis === 'x' ? profile.Wx : profile.Wy;

    let resultsHTML = '';
    SCENARIOS.forEach(sc => {
      const r = computeScenario(sc, load, length, I, W, profile.M);
      const ok = r.sigma <= YIELD_STRENGTH;
      resultsHTML += `
        <tr>
          <td style="padding:12px;border-bottom:1px solid #e0e0e0;font-weight:600;">${sc.groupName} – ${sc.name}</td>
          <td style="padding:12px;border-bottom:1px solid #e0e0e0;text-align:center;">${r.dg.toFixed(2)} mm</td>
          <td style="padding:12px;border-bottom:1px solid #e0e0e0;text-align:center;">${r.df.toFixed(2)} mm</td>
          <td style="padding:12px;border-bottom:1px solid #e0e0e0;text-align:center;font-weight:700;">${r.total.toFixed(1)} mm</td>
          <td style="padding:12px;border-bottom:1px solid #e0e0e0;text-align:center;">${r.sigma.toFixed(1)} N/mm²</td>
          <td style="padding:12px;border-bottom:1px solid #e0e0e0;text-align:center;color:${ok ? '#2e7d32' : '#c62828'}">${ok ? '✓ Within yield' : '⚠ Exceeds yield'}</td>
        </tr>`;
    });

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Deflection Report - ${profile.name}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #333; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #005AB5; padding-bottom: 20px; margin-bottom: 30px; }
          .logo { font-size: 28px; font-weight: 700; color: #005AB5; }
          .logo span { color: #F2DC20; }
          .date { color: #666; font-size: 14px; }
          h1 { font-size: 22px; color: #282828; margin-bottom: 5px; }
          .subtitle { color: #666; margin-bottom: 30px; }
          .params { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 30px; }
          .param { background: #f8f9fa; padding: 15px; border-radius: 8px; }
          .param-label { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 5px; }
          .param-value { font-size: 18px; font-weight: 600; color: #333; }
          table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
          th { background: #005AB5; color: white; padding: 12px; text-align: left; font-size: 13px; }
          td { font-size: 13px; }
          .selfweight-note { font-size: 12px; color: #555; margin-bottom: 30px; }
          .specs { margin-top: 30px; padding: 20px; background: #f8f9fa; border-radius: 8px; }
          .specs h3 { margin-top: 0; font-size: 16px; }
          .specs-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
          .spec-item { font-size: 13px; }
          .spec-item strong { color: #005AB5; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e0e0e0; font-size: 12px; color: #888; text-align: center; }
          .buy-link { display: inline-block; margin-top: 20px; padding: 12px 24px; background: #005AB5; color: white; text-decoration: none; border-radius: 6px; font-weight: 600; }
          @media print { .buy-link { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">AME<span>System</span></div>
          <div class="date">Generated: ${new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
        </div>

        <h1>Deflection Calculation Report</h1>
        <p class="subtitle">Profile: ${profile.name}</p>

        <div class="params">
          <div class="param">
            <div class="param-label">Profile</div>
            <div class="param-value">${profile.id}</div>
          </div>
          <div class="param">
            <div class="param-label">Length (L)</div>
            <div class="param-value">${length} mm</div>
          </div>
          <div class="param">
            <div class="param-label">Load (F)</div>
            <div class="param-value">${load} N</div>
          </div>
          <div class="param">
            <div class="param-label">Section Properties</div>
            <div class="param-value">I${axis} = ${I} cm⁴, W${axis} = ${W} cm³</div>
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th>Load Case</th>
              <th style="text-align:center;">Own Weight d<sub>g</sub></th>
              <th style="text-align:center;">Acting Force d<sub>F</sub></th>
              <th style="text-align:center;">Total Deflection D</th>
              <th style="text-align:center;">Bending Stress σ</th>
              <th style="text-align:center;">Status</th>
            </tr>
          </thead>
          <tbody>
            ${resultsHTML}
          </tbody>
        </table>
        <p class="selfweight-note">Total deflection D = d<sub>g</sub> + d<sub>F</sub> includes the contribution from the profile's self-weight (F<sub>g</sub> = M·L·g).</p>

        <div class="specs">
          <h3>Profile Specifications - ${profile.name}</h3>
          <div class="specs-grid">
            <div class="spec-item"><strong>Ix:</strong> ${profile.Ix} cm⁴</div>
            <div class="spec-item"><strong>Iy:</strong> ${profile.Iy} cm⁴</div>
            <div class="spec-item"><strong>Wx:</strong> ${profile.Wx} cm³</div>
            <div class="spec-item"><strong>Wy:</strong> ${profile.Wy} cm³</div>
            <div class="spec-item"><strong>Area:</strong> ${profile.A} cm²</div>
            <div class="spec-item"><strong>Mass:</strong> ${profile.M} kg/m</div>
          </div>
        </div>

        <div style="text-align:center;">
          <a href="${BASE_URL}${profile.url}" class="buy-link">Configure & Order This Profile →</a>
        </div>

        <div class="footer">
          <p>E = 70,000 N/mm² (Aluminium) | g = 9.81 m/s² | Yield point = ${YIELD_STRENGTH} N/mm² | Results are theoretical estimates. Consult an engineer for critical applications.</p>
          <p><strong>AME System Australia</strong> | amesystem.com.au</p>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  }

  // ============ DEFLECTION CHART ============
  function updateDeflectionChart() {
    const canvas = document.getElementById('dc-chart');
    if (!canvas || !lastCalc) return;

    const scenario = getScenario(document.getElementById('dc-chart-scenario')?.value) || getScenario('central-supported');
    const { profile, length, load, I, W } = lastCalc;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const padding = { top: 30, right: 20, bottom: 40, left: 50 };

    ctx.clearRect(0, 0, width, height);

    const maxLen = Math.max(5000, length);
    const lengths = [];
    const deflections = [];

    // Generate data points (total deflection incl. self-weight)
    for (let l = 500; l <= maxLen; l += 100) {
      lengths.push(l);
      deflections.push(computeScenario(scenario, load, l, I, W, profile.M).total);
    }

    const maxDef = Math.max(...deflections);

    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    // Draw grid
    ctx.strokeStyle = '#e5e5e5';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = padding.top + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(padding.left, y);
      ctx.lineTo(width - padding.right, y);
      ctx.stroke();
    }

    // Draw axes
    ctx.strokeStyle = '#999';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(padding.left, padding.top);
    ctx.lineTo(padding.left, height - padding.bottom);
    ctx.lineTo(width - padding.right, height - padding.bottom);
    ctx.stroke();

    // Draw curve
    ctx.strokeStyle = '#005AB5';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    lengths.forEach((l, i) => {
      const x = padding.left + (l / maxLen) * chartWidth;
      const y = padding.top + chartHeight - (deflections[i] / maxDef) * chartHeight;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });
    ctx.stroke();

    // Draw current point
    const currentDef = computeScenario(scenario, load, length, I, W, profile.M).total;
    const cx = padding.left + (length / maxLen) * chartWidth;
    const cy = padding.top + chartHeight - (currentDef / maxDef) * chartHeight;

    ctx.fillStyle = '#F2DC20';
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Labels
    ctx.fillStyle = '#666';
    ctx.font = '11px -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Length L (mm)', width / 2, height - 8);

    ctx.save();
    ctx.translate(12, height / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Deflection D (mm)', 0, 0);
    ctx.restore();

    // X-axis labels
    ctx.textAlign = 'center';
    for (let i = 1; i <= 5; i++) {
      const val = Math.round((maxLen * i) / 5 / 100) * 100;
      const x = padding.left + (val / maxLen) * chartWidth;
      ctx.fillText(val.toString(), x, height - padding.bottom + 15);
    }

    // Y-axis labels
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const val = (maxDef / 5) * (5 - i);
      const y = padding.top + (chartHeight / 5) * i;
      ctx.fillText(val.toFixed(1), padding.left - 8, y + 4);
    }

    // Current value label
    ctx.fillStyle = '#333';
    ctx.font = 'bold 11px -apple-system, sans-serif';
    ctx.textAlign = cx > width - 150 ? 'right' : 'left';
    ctx.fillText(`${length}mm → ${currentDef.toFixed(1)}mm`, cx + (cx > width - 150 ? -10 : 10), cy - 8);
  }

  // ============ PROFILE RECOMMENDER ============
  function runRecommender() {
    const length = parseFloat(document.getElementById('dc-rec-length')?.value);
    const load = parseFloat(document.getElementById('dc-rec-load')?.value);
    const maxDef = parseFloat(document.getElementById('dc-rec-maxdef')?.value);
    const scenario = getScenario(document.getElementById('dc-rec-scenario')?.value) || getScenario('central-supported');

    const resultsEl = document.getElementById('dc-rec-results');
    if (!resultsEl) return;

    if (!length || !load || !maxDef) {
      resultsEl.innerHTML = '<p class="dc-rec-empty">Enter your requirements above to find matching profiles.</p>';
      return;
    }

    const allProfiles = getAllProfiles();
    const results = [];

    allProfiles.forEach(profile => {
      // Check both axes
      ['x', 'y'].forEach(axis => {
        const I = axis === 'x' ? profile.Ix : profile.Iy;
        const W = axis === 'x' ? profile.Wx : profile.Wy;
        const r = computeScenario(scenario, load, length, I, W, profile.M);

        if (r.total <= maxDef) {
          results.push({
            profile,
            axis,
            I,
            deflection: r.total,
            sigma: r.sigma,
            margin: ((maxDef - r.total) / maxDef * 100).toFixed(0)
          });
        }
      });
    });

    // Sort by weight (lightest first that meets requirements)
    results.sort((a, b) => a.profile.M - b.profile.M);

    if (results.length === 0) {
      resultsEl.innerHTML = `
        <div class="dc-rec-empty">
          <p>No profiles found that meet your requirements.</p>
          <p style="font-size:12px;color:#888;margin-top:8px;">Try increasing max deflection or reducing length/load.</p>
        </div>`;
      return;
    }

    let html = '<div class="dc-rec-list">';
    results.slice(0, 8).forEach((r, i) => {
      const ok = r.sigma <= YIELD_STRENGTH;
      html += `
        <div class="dc-rec-item ${i === 0 ? 'dc-rec-best' : ''}">
          ${i === 0 ? '<span class="dc-rec-badge">Best Match</span>' : ''}
          <div class="dc-rec-item-header">
            <span class="dc-rec-item-name">${r.profile.name}</span>
            <span class="dc-rec-item-axis">${r.axis.toUpperCase()}-axis</span>
          </div>
          <div class="dc-rec-item-stats">
            <div class="dc-rec-stat">
              <span class="dc-rec-stat-label">Deflection D</span>
              <span class="dc-rec-stat-value">${r.deflection.toFixed(1)} mm</span>
            </div>
            <div class="dc-rec-stat">
              <span class="dc-rec-stat-label">Stress σ</span>
              <span class="dc-rec-stat-value">${r.sigma.toFixed(1)} N/mm²</span>
            </div>
            <div class="dc-rec-stat">
              <span class="dc-rec-stat-label">Weight</span>
              <span class="dc-rec-stat-value">${r.profile.M} kg/m</span>
            </div>
            <div class="dc-rec-stat">
              <span class="dc-rec-stat-label">Margin</span>
              <span class="dc-rec-stat-value dc-rec-margin">+${r.margin}%</span>
            </div>
          </div>
          <div class="dc-rec-item-footer">
            <span class="dc-assess ${ok ? 'dc-excellent' : 'dc-high'}" style="margin:0;padding:4px 10px;font-size:11px;">${ok ? '✓ Within yield strength' : '⚠ Exceeds yield strength'}</span>
            <a href="${BASE_URL}${r.profile.url}" target="_blank" class="dc-rec-buy">View & Order →</a>
          </div>
        </div>`;
    });
    html += '</div>';

    if (results.length > 8) {
      html += `<p style="text-align:center;font-size:12px;color:#888;margin-top:15px;">${results.length - 8} more profiles also match your requirements.</p>`;
    }

    resultsEl.innerHTML = html;
  }

  // ============ RECENT CALCULATIONS ============
  function saveToRecent(calc) {
    try {
      let recent = JSON.parse(localStorage.getItem('dc_recent') || '[]');
      recent = recent.filter(r => !(r.profile === calc.profile && r.length === calc.length && r.load === calc.load));
      recent.unshift(calc);
      recent = recent.slice(0, 5);
      localStorage.setItem('dc_recent', JSON.stringify(recent));
      renderRecent();
    } catch (e) {}
  }

  function renderRecent() {
    const container = document.getElementById('dc-recent-list');
    if (!container) return;

    try {
      const recent = JSON.parse(localStorage.getItem('dc_recent') || '[]');
      if (recent.length === 0) {
        container.innerHTML = '<p class="dc-recent-empty">No recent calculations yet.</p>';
        return;
      }

      let html = '';
      recent.forEach(r => {
        html += `
          <button class="dc-recent-item" data-profile="${r.profile}" data-length="${r.length}" data-load="${r.load}" data-axis="${r.axis}">
            <span class="dc-recent-name">${r.profileName}</span>
            <span class="dc-recent-params">${r.length}mm / ${r.load}N</span>
          </button>`;
      });
      container.innerHTML = html;

      // Add click handlers
      container.querySelectorAll('.dc-recent-item').forEach(btn => {
        btn.addEventListener('click', () => {
          const profileId = btn.dataset.profile;
          for (const [seriesKey, seriesData] of Object.entries(PROFILE_DATABASE)) {
            const found = seriesData.profiles.find(p => p.id === profileId);
            if (found) {
              document.getElementById('dc-series').value = seriesKey;
              updateProfileDropdown(seriesKey);
              setTimeout(() => {
                document.getElementById('dc-profile').value = profileId;
                document.getElementById('dc-length').value = btn.dataset.length;
                document.getElementById('dc-load').value = btn.dataset.load;
                document.getElementById('dc-axis').value = btn.dataset.axis;
                performCalculation();
                document.getElementById('dc-inputs').scrollIntoView({ behavior: 'smooth' });
              }, 50);
              break;
            }
          }
        });
      });
    } catch (e) {}
  }

  // ============ TABS ============
  function switchTab(tabId) {
    document.querySelectorAll('.dc-tab').forEach(t => t.classList.remove('dc-tab-active'));
    document.querySelectorAll('.dc-tab-content').forEach(c => c.classList.remove('dc-tab-content-active'));
    document.querySelector(`.dc-tab[data-tab="${tabId}"]`)?.classList.add('dc-tab-active');
    document.getElementById(`dc-tab-${tabId}`)?.classList.add('dc-tab-content-active');
  }

  // ============ INIT ============
  function init() {
    // Build the six scenario result cards
    buildCards();

    // Series change
    document.getElementById('dc-series')?.addEventListener('change', function() {
      updateProfileDropdown(this.value);
    });

    // All inputs trigger calculation
    ['dc-series', 'dc-profile', 'dc-axis'].forEach(id => {
      document.getElementById(id)?.addEventListener('change', performCalculation);
    });

    ['dc-length', 'dc-load'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', performCalculation);
        el.addEventListener('change', performCalculation);
      }
    });

    // Chart scenario selector
    document.getElementById('dc-chart-scenario')?.addEventListener('change', updateDeflectionChart);

    // Share URL copy
    document.getElementById('dc-copy-btn')?.addEventListener('click', copyShareURL);

    // PDF export
    document.getElementById('dc-pdf-btn')?.addEventListener('click', exportPDF);

    // Recommender inputs
    ['dc-rec-length', 'dc-rec-load', 'dc-rec-maxdef'].forEach(id => {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener('input', runRecommender);
        el.addEventListener('change', runRecommender);
      }
    });
    document.getElementById('dc-rec-scenario')?.addEventListener('change', runRecommender);

    // Tabs
    document.querySelectorAll('.dc-tab').forEach(tab => {
      tab.addEventListener('click', () => switchTab(tab.dataset.tab));
    });

    // Load from URL if params present
    loadFromURL();

    // Render recent
    renderRecent();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
